
import { defineComponent, computed, watch, onBeforeUnmount } from 'vue'
import { useCrmMobilePullToRefresh } from '@/composables/useCrmMobilePullToRefresh'
import CrmMobilePullRefreshIndicator from '@/components/Spa/CrmMobilePullRefreshIndicator.vue'

/**
 * Мобильный pull-to-refresh для CRM (window scroll).
 * Включается на узком экране / forced mobile, когда показан основной shell.
 */
export default defineComponent({
  name: 'CrmPullToRefreshHost',
  components: { CrmMobilePullRefreshIndicator },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    mobile: {
      type: Boolean,
      default: false,
    },
    drawerOpen: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const enabled = computed(
      () =>
        props.active &&
        props.mobile &&
        !props.drawerOpen &&
        typeof window !== 'undefined' &&
        'ontouchstart' in window,
    )

    const { pullVisualPx, pullArmed } = useCrmMobilePullToRefresh(enabled)

    /**
     * Смещаем только основной контент (.v-main), НЕ .v-application.
     * Transform на .v-application делает position:fixed у нижнего меню
     * относительно приложения — меню «отрывается» от низа экрана (iOS PWA).
     */
    function getPullOffsetTarget(): HTMLElement | null {
      return document.querySelector('.v-main') as HTMLElement | null
    }

    function clearAppOffset() {
      const target = getPullOffsetTarget()
      if (target) {
        target.style.transform = ''
        target.style.transition = ''
      }
      // На случай старых сессий, где transform оставался на корне
      const app = document.querySelector('.v-application') as HTMLElement | null
      if (app) {
        app.style.transform = ''
        app.style.transition = ''
      }
      const wrap = document.querySelector('.v-application--wrap') as HTMLElement | null
      if (wrap) {
        wrap.style.transform = ''
        wrap.style.transition = ''
      }
    }

    watch(
      pullVisualPx,
      (px, prev) => {
        const target = getPullOffsetTarget()
        if (!target) {
          return
        }
        if (px > 0) {
          target.style.transition = 'none'
          target.style.transform = `translateY(${Math.round(px)}px)`
        } else {
          target.style.transition = 'transform 0.18s ease'
          target.style.transform = ''
          // После отпускания жеста — переприжать нижнее меню
          if (prev && prev > 0) {
            window.dispatchEvent(new CustomEvent('crm:pin-bottom-nav'))
          }
        }
      },
      { flush: 'sync' },
    )

    onBeforeUnmount(() => {
      clearAppOffset()
    })

    return {
      pullVisualPx,
      pullArmed,
    }
  },
})
